"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Optimization() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tales = [{
    id: 1,
    badge: "Performance",
    title: "Lightning Fast Load Times",
    teaser: "Optimize your hotel website architecture to ensure sub-second loading, keeping guests engaged and reducing bounce rates.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_speed_optimization.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_speed_optimization.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_speed_optimization.png-wvc-sizes",
    alt: "Digital visualization of high-speed data flow for hotel websites",
    rotation: "-rotate-1",
    rotationMobile: "-rotate-[0.5deg]",
    offset: "md:translate-y-0"
  }, {
    id: 2,
    badge: "Conversion",
    title: "Direct Booking Engines",
    teaser: "Streamline your booking funnel with intuitive UI/UX design that turns casual visitors into confirmed hotel reservations.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_funnel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_funnel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_booking_funnel.png-wvc-sizes",
    alt: "Graphic showing a simplified path to hotel booking conversion",
    rotation: "rotate-[1.5deg]",
    rotationMobile: "rotate-[0.75deg]",
    offset: "md:translate-y-6"
  }, {
    id: 3,
    badge: "Mobile",
    title: "Seamless Mobile Experience",
    teaser: "Capture the mobile-first traveler with responsive designs that look stunning and function perfectly on every device.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mobile_responsive.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mobile_responsive.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_mobile_responsive.png-wvc-sizes",
    alt: "Smartphone displaying a modern hotel booking interface",
    rotation: "-rotate-[0.8deg]",
    rotationMobile: "-rotate-[0.4deg]",
    offset: "md:-translate-y-3"
  }];
  return <section data-section-id="11789" id="website-development-optimization-services" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        {}
        <div ref={introRef} className={`mx-auto max-w-[680px] text-center transition-all duration-[620ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3.5"}`}>
          <p className="font-default text-[0.8125rem] md:text-[0.9375rem] font-extrabold uppercase tracking-[0.08em] text-foreground/70">
            Development & Optimization
          </p>
          <h2 className="font-default mt-4 text-[clamp(2rem,4.2vw,3.5rem)] font-medium leading-[1.08] tracking-[-0.015em] text-foreground">
            Digital solutions that{" "}
            <span className="italic text-primary">convert</span>
          </h2>
          <p className="font-default mt-5 text-[clamp(1.125rem,1.4vw,1.3125rem)] leading-[1.6] text-foreground/80">
            High-performance web development tailored to drive direct revenue for modern hotels.
          </p>

          {}
          <div className="mt-8 flex justify-center">
            <svg width="80" height="14" viewBox="0 0 80 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="opacity-60">
              <path d="M2 7 Q 12 1, 22 7 T 42 7 T 62 7 T 78 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" className="text-foreground" />
            </svg>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6">
          {tales.map((tale, i) => <div key={tale.id} data-index={i} className={`md:col-span-4 ${tale.offset}`}>
              <Link to="/HighConvertingHotelWebsiteDesignOptimizationPage" className="group block">
                <Card className={`bg-card text-card-foreground transform ${tale.rotationMobile} md:${tale.rotation} transition-all duration-[320ms] ease-out group-hover:rotate-0 group-hover:shadow-lg border border-border rounded-lg overflow-hidden pt-0`}>
                  {}
                  <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                    <img src={tale.image} data-wvc-srcset={tale.srcset} data-wvc-sizes={tale.sizes} alt={tale.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[320ms] ease-out group-hover:scale-[1.03]" />
                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-multiply" style={{
                  backgroundImage: "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.4) 0.5px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(0,0,0,0.3) 0.5px, transparent 1px)",
                  backgroundSize: "6px 6px, 9px 9px"
                }} aria-hidden="true" />
                  </div>

                  <div className="px-6 md:px-7 pb-7 pt-6">
                    <Badge variant="secondary" className="bg-secondary/15 text-secondary border-transparent font-bold tracking-wide rounded-lg px-2.5 py-1">
                      {tale.badge}
                    </Badge>

                    <h3 className="font-default mt-4 text-[clamp(1.375rem,2.2vw,1.875rem)] font-semibold leading-[1.2] tracking-[-0.01em] text-card-foreground">
                      {tale.title}
                    </h3>

                    <p className="font-default mt-3 text-[1rem] leading-[1.65] text-card-foreground/80 line-clamp-2">
                      {tale.teaser}
                    </p>

                    {}
                    <div className="mt-5 inline-flex items-center gap-2 text-primary">
                      <span className="font-default text-sm font-extrabold tracking-wide relative">
                        View service details
                        <svg className="absolute -bottom-1 left-0 w-full" height="6" viewBox="0 0 120 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M2 4 Q 30 1, 60 3 T 118 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" strokeDasharray="200" strokeDashoffset="200" className="transition-all duration-700 ease-out group-hover:[stroke-dashoffset:0]" />
                        </svg>
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-[320ms] ease-out group-hover:translate-x-1.5" strokeWidth={1.75} />
                    </div>
                  </div>
                </Card>
              </Link>
            </div>)}
        </div>
      </div>
    </section>;
}